import React, { useEffect, useState } from 'react';
import { GetServerSidePropsContext } from 'next';
import { useAsync, useEffectOnce } from 'react-use';
import NextLink from 'next/link';
import { always, flatten, pick, values } from 'ramda';
import { useRouter } from 'next/router';
import { FormattedMessage } from 'react-intl';

import { Artwork, Meta } from '@definitions/artworks.types';
import api from '@services/api';
import reportError from '@utils/reportError';
import { getPreviewToken, redirectOnError } from '@utils/helpers';
import { ARTWORKS_LIMIT } from '@modules/plp/plp.constants';
import { ArtworksView } from '@components/PLP/artworksView';
import { Header } from '@components/header';
import { useChangeGridSize, useFetchArtworks, useSetCurrentPage, useSetInitialArtworks } from '@hooks/useArtworks';
import { GRID_SIZE, PLP_VARIANT } from '@modules/plp';
import { ArtworksGrid } from '@components/PLP/artworksGrid';
import { renderWhenTrue, renderWhenTrueOtherwise } from '@utils/rendering';
import { useGroupings } from '@hooks/useHeader';
import { EXPERIMENT_ID, MAIN_ANIMATION, ROUTE } from '@utils/constants';
import { useFilters } from '@hooks/useFilters';
import { Grouping as GroupingType } from '@definitions/header.types';
import { HEADER_TOP } from '@components/header/header.constants';
import { DROPDOWN_TYPES, FILTER_BUTTON_TEST_ID } from '@components/PLP/artworksView/artworksView.component';
import { FocusTrap } from '@components/common';
import { HeaderFilter } from '@components/headerFilter';
import { GridSwitcher } from '@components/PLP/gridSwitcher';
import { FILTER_ORDER } from '@components/headerFilter/headerFilter.constants';
import { useHeaderMobileMenu } from '@hooks/useUi/useUi';
import { useGoogleOptimize } from '@hooks/useGoogleOptimize';
import {
  Container,
  MenuOption,
  MotionContainer,
  DesktopGroupingsContainer,
  GroupingsList,
  Grouping,
  ControlsContainer,
  MobileGroupingsButton,
  GroupingIntro,
  GroupingTitle,
  GroupingDescription,
  TradeProgram,
} from '@components/PLP/artworksView/artworksView.styles';
import { ArtistDesignedProducts } from '@components/PLP/artistsDesignedProducts';

const INITIAL_OFFSET = 0;

export const getServerSideProps = async ({ req, query }: GetServerSidePropsContext) => {
  const { page = 1, grouping, ...restQuery } = query;

  try {
    const token = getPreviewToken(req.headers.cookie);
    const { price, inputPrice, ...restParams } = restQuery as { price: string[] | string; inputPrice: string };
    const newParams: { price?: string | string[] } = { ...(restParams || {}), price };
    if (inputPrice) {
      if (price) {
        newParams.price = typeof price === 'string' ? [price, inputPrice] : [...price, inputPrice];
      } else {
        newParams.price = inputPrice;
      }
    }

    const params = {
      limit: ARTWORKS_LIMIT * +page,
      offset: INITIAL_OFFSET,
      ...newParams,
    };

    if (grouping && typeof grouping === 'string') {
      const { data } = await api.get(`/artworks/groupings/${grouping}/`, {
        params,
        headers: { cookie: req.headers.cookie || '', authorization: token },
      });
      return {
        props: { initialArtworks: data.results, meta: { count: data.count, hasNext: !!data.next } },
      };
    }
  } catch (e) {
    reportError(e);

    return redirectOnError(e);
  }
};

interface SlugProps {
  initialArtworks: Artwork[];
  meta: Meta;
}

export default function Slug({ initialArtworks, meta }: SlugProps) {
  const [dropdownOpen, setDropdownOpen] = useState<DROPDOWN_TYPES | null>(null);
  const [filtersCount, setFiltersCount] = useState(0);
  const [groupingIntro, setGroupingIntro] = useState({
    description: '',
    title: '',
  });
  const { artworks, setInitialArtworks } = useSetInitialArtworks(PLP_VARIANT.GROUPINGS);
  const { groupings, fetchGroupings } = useGroupings();
  const { fetchArtworks, meta: groupingsMeta, loading } = useFetchArtworks(PLP_VARIANT.GROUPINGS);
  const { currentPage } = useSetCurrentPage(PLP_VARIANT.GROUPINGS);
  const { gridSize, changeGridSize } = useChangeGridSize(PLP_VARIANT.GROUPINGS);
  const router = useRouter();
  const { setHeaderMobileMenuOpen } = useHeaderMobileMenu();
  const groupingSlug = router.query.grouping as string;
  const { filterOptions, fetchFilterOptions } = useFilters(groupingSlug);
  const isFilterOpen = dropdownOpen === DROPDOWN_TYPES.FILTER;
  const isFavoriteOpen = dropdownOpen === DROPDOWN_TYPES.FAVOURITE;
  const variant = useGoogleOptimize(EXPERIMENT_ID);
  let updatedGroupings;

  useEffectOnce(() => {
    setInitialArtworks({ artworks: initialArtworks, meta });
  });

  useEffect(() => {
    setFiltersCount(flatten(values(pick([...FILTER_ORDER, 'inputPrice'], router.query))).length);
  }, [router.query]);

  useEffect(() => {
    if (groupings) {
      const currentItem = groupings.filter((i) => i.slug === groupingSlug)[0];
      setGroupingIntro({
        description: currentItem?.description || '',
        title: currentItem?.name,
      });
    }
  }, [groupingSlug, groupings]);

  const allArtwork = {
    name: 'All Artworks',
    description: '',
    default: false,
    slug: 'artworks',
    seoPageTitle: '',
    seoDescription: '',
    seoTags: [],
    artistDesignedProductsLayout: false,
  };
  if (groupings && groupings.length > 0) {
    updatedGroupings = [allArtwork, ...groupings];
  } else {
    updatedGroupings = [allArtwork];
  }

  useAsync(async () => {
    await fetchGroupings();
  });

  const determineGridSizeAB = () => {
    return variant === 0 ? GRID_SIZE.SMALL : GRID_SIZE.MEDIUM;
  };

  const toggleDropdown = (type: DROPDOWN_TYPES) => () =>
    type === dropdownOpen ? setDropdownOpen(null) : setDropdownOpen(type);

  const renderFiltersCount = renderWhenTrue(always(`: ${filtersCount}`));

  const renderFocusTrap = renderWhenTrue(
    always(<FocusTrap onFocus={() => setDropdownOpen(DROPDOWN_TYPES.FAVOURITE)} />)
  );

  const renderMobileGroupingsButton = renderWhenTrue(
    always(
      <MobileGroupingsButton onClick={() => setHeaderMobileMenuOpen(true)}>{groupingIntro.title}</MobileGroupingsButton>
    )
  );

  const renderGroupingIntro = renderWhenTrue(
    always(
      <GroupingIntro>
        <GroupingTitle>{groupingIntro.title}</GroupingTitle>
        <GroupingDescription dangerouslySetInnerHTML={{ __html: groupingIntro.description }}></GroupingDescription>
      </GroupingIntro>
    )
  );

  const renderTradeProgramLink = (idx: number) => (
    <NextLink key={idx} href={ROUTE.TRADE} passHref>
      <TradeProgram isActive={router.pathname === ROUTE.TRADE}>
        <FormattedMessage id="artworksView.tradeProgram" defaultMessage="Trade Program" />
      </TradeProgram>
    </NextLink>
  );

  const renderGroupingLink = (grouping: GroupingType, idx: number) => (
    <Grouping
      isActive={
        grouping.name === 'All Artworks'
          ? router.asPath === ROUTE.ARTWORKS
          : router.asPath === `${ROUTE.ARTWORKS}${ROUTE.GROUPINGS}/${grouping.slug}`
      }
    >
      {grouping.name === 'All Artworks' ? (
        <a href={`${ROUTE.ARTWORKS}`}>{grouping.name}</a>
      ) : (
        <a href={`${ROUTE.ARTWORKS}${ROUTE.GROUPINGS}/${grouping.slug}`}>{grouping.name}</a>
      )}
    </Grouping>
  );

  const renderSecNavBarLink = (grouping: GroupingType, idx: number) => {
    return grouping.slug == 'trade' ? renderTradeProgramLink(idx) : renderGroupingLink(grouping, idx);
  };

  const currentGrouping = groupings?.filter((i) => i.slug === groupingSlug)[0];

  const renderGroupings = renderWhenTrue(
    always(
      <GroupingsList isHidden={!groupings || groupings.length === 0}>
        {updatedGroupings?.map((grouping, idx) => renderSecNavBarLink(grouping, idx))}
      </GroupingsList>
    )
  );

  const renderControls = renderWhenTrue(() => (
    <ControlsContainer isFilterSelected={!!filtersCount}>
      <GridSwitcher initialGridSize={determineGridSizeAB()} gridSize={gridSize} changeGridSize={changeGridSize} />

      <MenuOption onClick={toggleDropdown(DROPDOWN_TYPES.FILTER)} data-testid={FILTER_BUTTON_TEST_ID}>
        <FormattedMessage id="artworksView.filterMenu" defaultMessage="Filter" />
        {renderFiltersCount(!!filtersCount)}
      </MenuOption>
    </ControlsContainer>
  ));

  const renderCustomHeader = () => (
    <Header
      headerTop={HEADER_TOP.REGULAR}
      childrenSize={51}
      isChildrenOpen={isFilterOpen}
      handleChildrenClose={() => setDropdownOpen(null)}
    >
      <MotionContainer
        initial={{ opacity: 0, y: -44 }}
        animate={{ opacity: 1, y: 0, transition: { duration: MAIN_ANIMATION.DURATION } }}
        exit={{ opacity: 0, y: -44, transition: { duration: MAIN_ANIMATION.DURATION } }}
      >
        <Container>
          <DesktopGroupingsContainer isArtistDesignedView={currentGrouping?.artistDesignedProductsLayout}>
            {renderGroupings(!!groupings)}
          </DesktopGroupingsContainer>

          {renderMobileGroupingsButton(router.pathname !== ROUTE.ARTWORKS)}

          {renderControls(!currentGrouping?.artistDesignedProductsLayout)}
        </Container>
        {renderFocusTrap(isFavoriteOpen)}
        <HeaderFilter
          groupingSlug={groupingSlug}
          options={filterOptions}
          open={isFilterOpen}
          onClose={() => setDropdownOpen(null)}
        />
      </MotionContainer>
    </Header>
  );

  const contextProps = {
    meta: groupingsMeta,
    loading,
    fetchArtworks,
    currentPage,
  };

  const commonProps = {
    plp: PLP_VARIANT.GROUPINGS,
    groupingsIntro: renderGroupingIntro(!!groupings),
    artworks: artworks,
    gridSize: gridSize,
    initialGridSize: determineGridSizeAB(),
  };

  const renderLayout = renderWhenTrueOtherwise(
    () => <ArtistDesignedProducts {...commonProps} artworks={initialArtworks} context={contextProps} />,
    () => <ArtworksGrid {...commonProps} context={contextProps} />
  );

  let seoImageUrl = null;
  if (initialArtworks.length > 0 && !!initialArtworks[0].images) {
    seoImageUrl = {
      tags: [],
      title: '',
      noIndex: false,
      noFollow: false,
      image: {
        url: initialArtworks[0].images[0].urlThumbnail,
        alt: initialArtworks[0].images[0].imageFile,
      },
    };
  }

  return (
    <ArtworksView
      fetchFiltersFn={fetchFilterOptions}
      customHeader={() => renderCustomHeader()}
      seoImageUrl={seoImageUrl}
    >
      {renderLayout(currentGrouping?.artistDesignedProductsLayout)}
    </ArtworksView>
  );
}
